import { z } from 'zod';
import { BuildSettingsSchema } from './build';
import { GlossarySettingsSchema } from './glossary';
import { QuoteSettingsSchema } from './quote';

export enum AutoReplyDraftTemplateType {
  ALL_IN = 'All In',
  ALL_LINE_ITEMS = 'All Line Items',
  LINEHAUL = 'Linehaul',
  LINEHAUL_AND_FUEL = 'Linehaul and Fuel',
  RPM = 'RPM',
  RPM_EXCLUDE_FUEL = 'RPM Excluding Fuel',
}

export const AutoReplyDraftTemplateTypeValues = Object.values(
  AutoReplyDraftTemplateType
) as [string, ...string[]];

export const AutoReplySettingsSchema = z
  .object({
    whenToRespond: z
      .array(z.string())
      .optional()
      .nullable()
      .describe('List of go/no-go conditions for auto-reply'),
    draftingInstructions: z
      .string()
      .optional()
      .nullable()
      .describe('AI instructions for drafting quote responses'),
    ccRecipients: z
      .array(z.string())
      .optional()
      .nullable()
      .describe('List of email addresses to CC on auto-replies'),
    bccRecipients: z
      .array(z.string())
      .optional()
      .nullable()
      .describe('List of email addresses to BCC on auto-replies'),
    draftTemplateType: z
      .enum(AutoReplyDraftTemplateTypeValues)
      .optional()
      .nullable()
      .describe('The template type we used to draft auto-replies'),
    actionDefinitionId: z
      .string()
      .optional()
      .nullable()
      .describe(
        'ID of agent SOP (action definition) that handles auto-replies'
      ),
    sendDelaySeconds: z
      .number()
      .int()
      .min(0)
      .optional()
      .nullable()
      .describe('Delay before sending a quote auto-reply, in seconds.'),
  })
  .strict();

export type AutoReplySettings = z.infer<typeof AutoReplySettingsSchema>;

export const OriginatorTrackSettingsSchema = z
  .object({
    disallowBrokerageStatusUpdates: z
      .boolean()
      .optional()
      .nullable()
      .describe(
        "When true, automated TMS updates never change the brokerage status for this originator's loads"
      ),
  })
  .strict();

export type OriginatorTrackSettings = z.infer<
  typeof OriginatorTrackSettingsSchema
>;

export const OriginatorSettingsSchema = z
  .object({
    autoReply: AutoReplySettingsSchema.optional().nullable(),
    flaggedForTeamsNotifications: z.boolean().optional().nullable(),
    glossary: GlossarySettingsSchema.optional().nullable(),
    quote: QuoteSettingsSchema.optional().nullable(),
    build: BuildSettingsSchema.optional().nullable(),
    track: OriginatorTrackSettingsSchema.optional().nullable(),
  })
  .strict();

export type OriginatorSettings = z.infer<typeof OriginatorSettingsSchema>;
